import React from 'react';
import { motion } from 'framer-motion';
import { 
  LayoutGrid, 
  Box, 
  Lightbulb, 
  Droplets, 
  Flame, 
  Layers, 
  Armchair,
  ArrowUpRight
} from 'lucide-react';

const services = [
  {
    icon: LayoutGrid,
    title: '2D Layouts & Floor Plans',
    description: 'Precision-crafted floor plans that optimize space utilization and flow.',
  },
  {
    icon: Box,
    title: '3D Visualization & Walkthroughs',
    description: 'Immersive 3D renders bringing your vision to life before execution.',
  },
  {
    icon: Lightbulb,
    title: 'Electrical & Lighting Design',
    description: 'Strategic lighting solutions that enhance ambiance and functionality.',
  },
  {
    icon: Droplets,
    title: 'Plumbing Solutions',
    description: 'Integrated plumbing layouts for kitchens, bathrooms, and utilities.',
  },
  {
    icon: Flame,
    title: 'Fire Safety Systems',
    description: 'Compliant fire safety planning for residential and commercial spaces.',
  },
  {
    icon: Layers,
    title: 'False Ceiling Design',
    description: 'Elegant ceiling treatments that add depth and architectural interest.',
  },
  {
    icon: Armchair,
    title: 'Furniture Layout & Planning',
    description: 'Thoughtful furniture arrangements maximizing comfort and aesthetics.',
  },
];

export default function Services() {
  return (
    <section id="services" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[1px] bg-[#C9A962]" />
            <span className="text-[#C9A962] tracking-[0.3em] text-xs uppercase">What We Offer</span>
            <div className="w-12 h-[1px] bg-[#C9A962]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            Our Interior Design Services
          </h2>
          <p className="text-[#2C2C2C]/60 max-w-2xl mx-auto text-lg">
            Comprehensive design solutions from concept to completion, tailored to your unique requirements.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-[#FDFBF7] p-8 border border-[#E8E2D9] hover:border-[#C9A962] transition-all duration-500 cursor-pointer overflow-hidden"
            >
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5 group-hover:opacity-10 transition-opacity">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <pattern id={`pattern-${index}`} patternUnits="userSpaceOnUse" width="20" height="20">
                    <rect width="1" height="1" fill="#C9A962" />
                  </pattern>
                  <rect width="100" height="100" fill={`url(#pattern-${index})`} />
                </svg>
              </div>

              <div className="relative z-10">
                <div className="w-14 h-14 bg-[#C9A962]/10 flex items-center justify-center mb-6 group-hover:bg-[#C9A962] transition-colors duration-500">
                  <service.icon className="w-6 h-6 text-[#C9A962] group-hover:text-white transition-colors duration-500" />
                </div>

                <h3 className="font-serif text-xl text-[#2C2C2C] mb-3 group-hover:text-[#C9A962] transition-colors">
                  {service.title}
                </h3>

                <p className="text-[#2C2C2C]/60 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>

                <div className="flex items-center text-[#C9A962] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn More
                  <ArrowUpRight className="ml-2 w-4 h-4" />
                </div>
              </div>
            </motion.div>
          ))}

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            className="relative bg-[#2C2C2C] p-8 flex flex-col justify-center items-center text-center overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=600&q=80"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <h3 className="font-serif text-2xl text-white mb-3">
                Have a Project in Mind?
              </h3>
              <p className="text-white/60 text-sm mb-6">
                Let's discuss how we can bring your vision to life.
              </p>
              <a
                href="#contact"
                className="inline-flex items-center bg-[#C9A962] text-white px-6 py-3 text-sm hover:bg-[#b8954f] transition-colors"
              >
                Start Your Project
                <ArrowUpRight className="ml-2 w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}